// Event categories for better analytics organization
export const EVENT_CATEGORIES = {
  MONETIZATION: {
    name: "Monetization",
    events: [
      "app_install",
      "onboarding_start",
      "onboarding_finish",
      "signup_success",
      "trial_start",
      "subscription_active",
      "paywall_view",
      "paywall_view_unique",
      "paywall_cta_press",
      "paywall_plan_selected",
      "paywall_configuration_error",
      "paywall_declined",
      "subscription_renewed",
      "subscription_cancelled",
      "subscription_refund",
      "subscription_mismatch",
      "entitlement_error",
      "retained_subscriber_regression",
      "winback_offer_view",
      "winback_offer_purchase",
      "winback_offer_card_view",
      "winback_notification_open",
    ],
  },
  ENGAGEMENT_NOTIFICATIONS: {
    name: "Engagement Notifications",
    events: [
      "notification_permission",
      "notification_scheduled",
      "notification_delivery_accepted",
      "notification_open",
      "notification_category_changed",
      "notification_guardrail_violation",
      "checkin_complete",
      "habit_checkin",
      "plan_started",
      "plan_continued",
      "activation_first_action",
      "return_to_active_usage",
      // Fires from ThePath navigation/index.tsx when a member opens the
      // WhatsApp group deep link, so it is re-engagement, not onboarding.
      "whatsapp_membership_link_opened",
    ],
  },
  LANDING: {
    name: "Landing Page",
    events: [
      "landing_view",
      "landing_view_unique",
      "landing_cta_press",
      "landing_store_press",
      "landing_waitlist_join",
      "landing_waitlist_already",
      "proposal_view",
      "proposal_cta_press",
    ],
  },
  USER_AUTH: {
    name: "Authentication",
    events: [
      "CREATE_USER",
      "LOGIN_USER",
      "signup_success",
      "PASSWORD_RESET",
      "CLICK_SIGNUP_BTN",
      "CLICK_LOGIN_BTN",
      "GO_LOGIN",
      "NO_ACCOUNT_LOGIN_EXPLORE",
    ],
  },
  PLAN_INTERACTION: {
    name: "Plan Engagement",
    events: [
      "SELECT_PLAN",
      "START_PLAN",
      "EXPLORE_PLAN_PREVIEW",
      "START_DAY_PLAN",
      "NO_PLANS_EXPLORE",
      "NO_USER_START_PLAN",
      "NO_USER_START_PLAN_LOGIN",
      "learning_placement_assigned",
      "learning_placement_corrected",
      "learning_plan_difficulty_changed",
    ],
  },
  STEP_INTERACTION: {
    name: "Step Engagement",
    events: [
      "START_STEP",
      "SELECT_STEP",
      "USER_LIKED_STEP",
      "SELECT_STEP_PATH",
      "GO_STEPS_LIST",
    ],
  },
  TIMER_ACTIVITY: {
    name: "Timer Usage",
    events: [
      "START_TIMER",
      "STOP_TIMER",
      "ADD_INTERVAL",
      "EDIT_TIMER",
      "ADD_TIMER",
      "GO_TIMER",
      "ON_COMPLETE_TIME_SETUP",
    ],
  },
  QUESTIONS: {
    name: "Questions & Community",
    events: [
      "START_QUESTION",
      "SEND_QUESTION",
      "CREATE_QUESTION_PLAN",
      "CREATE_QUESTION_AUTHOR",
      "GO_TO_QUESTIONS",
      "OPEN_ROOM",
      "GO_QUESTION_LIST",
    ],
  },
  CONTENT_ENGAGEMENT: {
    name: "Content Engagement",
    events: [
      "USER_LIKED_QUOTE",
      "USER_LIKED_QUOTE_HOME",
      "GO_QUOTES_LIST",
      "SEE_AUTHOR",
      "GO_AUTHOR",
      "GO_AUTHOR_LIST",
    ],
  },
  NAVIGATION: {
    name: "Navigation",
    events: [
      "NEXT_ROUTE",
      "GO_TO_EXPLORE",
      "EXPLORE_DETAILS",
      "KEEP_EXPLORING",
      "GO_ACTIONS",
    ],
  },
  PERMISSIONS: {
    name: "Permissions",
    events: ["GET_NOTIF_PERMISSION"],
  },
  ONBOARDING: {
    name: "Onboarding",
    events: [
      "QUESTIONNAIRE",
      "onboarding_question_answered",
      "personalizing_complete",
      "guide_selected",
      "interactive_chat_start",
      "interactive_chat_complete",
      "interactive_chat_dismiss",
      "lead_capture_view",
      // The WhatsApp coach opt-in slide inside the questionnaire results flow.
      // ThePath emits these from resultsFlow/WhatsAppOptInSlide.tsx and
      // screens/Onboarding-Intro/index.tsx.
      "whatsapp_opt_in_preflight",
      "whatsapp_opt_in_eligible",
      "whatsapp_opt_in_view",
      "whatsapp_opt_in_phone_start",
      "whatsapp_opt_in_consent_change",
      "whatsapp_opt_in_submit",
      "whatsapp_opt_in_validation_error",
      "whatsapp_opt_in_save_error",
      "whatsapp_opt_in_accept",
      "whatsapp_opt_in_skip",
    ],
  },
};

export const getCategoryForEvent = (eventName: string): string => {
  for (const [categoryKey, category] of Object.entries(EVENT_CATEGORIES)) {
    if (category.events.includes(eventName)) {
      return category.name;
    }
  }
  return "Other";
};

export const getEventsByCategory = (
  events: Array<{ event: string; count: number }>
): Record<string, number> => {
  const categoryTotals: Record<string, number> = {};

  events.forEach(({ event, count }) => {
    const category = getCategoryForEvent(event);
    categoryTotals[category] = (categoryTotals[category] || 0) + count;
  });

  return categoryTotals;
};

// Funnel definitions for conversion tracking
export const FUNNELS = {
  MONETIZATION_FUNNEL: {
    name: "App Install to Subscription",
    steps: [
      { event: "app_install", name: "App Installed" },
      { event: "onboarding_start", name: "Started Onboarding" },
      { event: "onboarding_finish", name: "Completed Onboarding" },
      { event: "signup_success", name: "Signed Up" },
      { event: "paywall_view", name: "Viewed Paywall" },
      { event: "trial_start", name: "Started Trial" },
      { event: "subscription_active", name: "Active Subscriber" },
    ],
  },
  SIGNUP_FUNNEL: {
    name: "Sign Up Funnel",
    steps: [
      { event: "CLICK_SIGNUP_BTN", name: "Clicked Sign Up" },
      { event: "CREATE_USER", name: "Created Account" },
      // Over-counts here: signup_success also fires when a returning user
      // re-authenticates via Google/Apple, and this dashboard path only sees
      // pre-aggregated counts so it cannot filter on params.isNewUser the way
      // mcp/src/lib/eventTaxonomy.ts does. Keep both copies in step.
      // This raw-count path also has no per-user dedupe, so step-to-step
      // rates can exceed 100%; the MCP get_funnel numbers are authoritative.
      { event: "signup_success", name: "Signup Success" },
      // The legacy QUESTIONNAIRE wire value is never emitted by current app
      // builds; onboarding_finish is the emitted completion event.
      { event: "onboarding_finish", name: "Completed Onboarding" },
    ],
  },
  PLAN_ENGAGEMENT_FUNNEL: {
    name: "Plan Engagement Funnel",
    steps: [
      { event: "EXPLORE_PLAN_PREVIEW", name: "Viewed Plan" },
      { event: "SELECT_PLAN", name: "Selected Plan" },
      { event: "START_PLAN", name: "Started Plan" },
      { event: "START_DAY_PLAN", name: "Started Day Plan" },
      { event: "START_STEP", name: "Started First Step" },
    ],
  },
  TIMER_USAGE_FUNNEL: {
    name: "Timer Usage Funnel",
    steps: [
      { event: "GO_TIMER", name: "Viewed Timer" },
      { event: "ON_COMPLETE_TIME_SETUP", name: "Setup Timer" },
      { event: "START_TIMER", name: "Started Timer" },
      { event: "STOP_TIMER", name: "Completed Timer" },
    ],
  },
  LANDING_FUNNEL: {
    name: "Landing Page Funnel",
    steps: [
      { event: "landing_view", name: "Viewed Landing" },
      { event: "landing_cta_press", name: "Pressed CTA" },
      { event: "app_install", name: "Installed App" },
    ],
  },
  PROPOSAL_FUNNEL: {
    name: "Coach Proposal Funnel",
    steps: [
      { event: "proposal_view", name: "Viewed Proposal" },
      { event: "proposal_cta_press", name: "Pressed CTA" },
      { event: "onboarding_start", name: "Tried the Demo" },
    ],
  },
  WINBACK_FUNNEL: {
    name: "Paywall Winback Funnel",
    steps: [
      { event: "paywall_view", name: "Viewed Paywall" },
      { event: "paywall_declined", name: "Declined Paywall" },
      { event: "winback_notification_open", name: "Opened Winback Notification" },
      { event: "winback_offer_view", name: "Saw Winback Offer" },
      { event: "winback_offer_purchase", name: "Bought Winback Offer" },
    ],
  },
};

export const calculateFunnelConversion = (
  events: Array<{ event: string; count: number }>,
  funnelKey: keyof typeof FUNNELS
): Array<{ step: string; count: number; conversionRate: number }> => {
  const funnel = FUNNELS[funnelKey];
  const eventCounts = new Map(events.map((e) => [e.event, e.count]));

  const funnelData = funnel.steps.map((step, index) => {
    const count = eventCounts.get(step.event) || 0;
    const firstStepCount = eventCounts.get(funnel.steps[0].event) || 1;
    const conversionRate = (count / firstStepCount) * 100;

    return {
      step: step.name,
      count,
      conversionRate: index === 0 ? 100 : conversionRate,
    };
  });

  return funnelData;
};
